import type { FormField } from '../../interfaces/form.interface';
import type { InputType } from '../../interfaces/input.interface';
import { InputType as ReactiveInputType } from '../../interfaces/input.interface';
import {
    ReactiveFieldFactory,
    type ReactiveFieldConditionsFactoryConfig,
    type ReactiveFieldExtraConfig,
} from '../factory/reactive-field-factory';

const FIRST_REPEATABLE_LINE_INDEX = 1;
const DEFAULT_REPEATABLE_START_ORDER = 1;
const ORDER_INCREMENT = 1;
const DEFAULT_REPEATABLE_FIELD_WIDTH = 3;

export interface ReactiveRepeatableFieldLabelContext<TModel> {
    readonly lineIndex: number;
    readonly columnIndex: number;
    readonly sourcePrefix: string;
    readonly column: ReactiveRepeatableFieldColumn<TModel>;
}

export type ReactiveRepeatableFieldLabelResolver<TModel> = string | ((context: ReactiveRepeatableFieldLabelContext<TModel>) => string);

export interface ReactiveRepeatableFieldContext<TModel> extends ReactiveRepeatableFieldLabelContext<TModel> {
    readonly name: string;
    readonly label: string;
    readonly order: number;
}

export interface ReactiveRepeatableFieldColumn<TModel> {
    readonly sourcePrefix: string;
    readonly label: ReactiveRepeatableFieldLabelResolver<TModel>;
    readonly width?: number;
    readonly type?: InputType;
    readonly conditions?: ReactiveFieldConditionsFactoryConfig;
    readonly extra?: ReactiveFieldExtraConfig<TModel>;
    readonly createField?: (context: ReactiveRepeatableFieldContext<TModel>) => FormField<TModel>;
}

export interface ReactiveRepeatableFieldFactoryConfig<TModel> {
    readonly lineCount: number;
    readonly columns: readonly ReactiveRepeatableFieldColumn<TModel>[];
    readonly startOrder?: number;
    readonly width?: number;
    readonly conditions?: ReactiveFieldConditionsFactoryConfig;
}

/** Builds indexed field names and orders for repeated line blocks. */
export class ReactiveRepeatableFieldFactory {
    public static createFields<TModel>(config: ReactiveRepeatableFieldFactoryConfig<TModel>): FormField<TModel>[] {
        const fields: FormField<TModel>[] = [];
        let order = config.startOrder ?? DEFAULT_REPEATABLE_START_ORDER;

        for (let lineIndex = FIRST_REPEATABLE_LINE_INDEX; lineIndex <= config.lineCount; lineIndex += ORDER_INCREMENT) {
            config.columns.forEach((column, columnIndex) => {
                const context = this.createContext({ config, column, columnIndex, lineIndex, order });
                const field = column.createField?.(context) ?? this.createDefaultField(context, config);

                fields.push(field);
                order += ORDER_INCREMENT;
            });
        }

        return fields;
    }

    public static indexedName(sourcePrefix: string, lineIndex: number): string {
        return `${sourcePrefix}${lineIndex}`;
    }

    public static indexedLabel(label: string, lineIndex: number): string {
        return `${label} ${lineIndex}`;
    }

    public static firstLineLabel(label: string, lineIndex: number): string {
        return lineIndex === FIRST_REPEATABLE_LINE_INDEX ? label : '';
    }

    private static createContext<TModel>(options: {
        readonly config: ReactiveRepeatableFieldFactoryConfig<TModel>;
        readonly column: ReactiveRepeatableFieldColumn<TModel>;
        readonly columnIndex: number;
        readonly lineIndex: number;
        readonly order: number;
    }): ReactiveRepeatableFieldContext<TModel> {
        const labelContext: ReactiveRepeatableFieldLabelContext<TModel> = {
            lineIndex: options.lineIndex,
            columnIndex: options.columnIndex,
            sourcePrefix: options.column.sourcePrefix,
            column: options.column,
        };

        return {
            ...labelContext,
            name: this.indexedName(options.column.sourcePrefix, options.lineIndex),
            label: this.resolveLabel(options.column.label, labelContext),
            order: options.order,
        };
    }

    private static resolveLabel<TModel>(
        label: ReactiveRepeatableFieldLabelResolver<TModel>,
        context: ReactiveRepeatableFieldLabelContext<TModel>,
    ): string {
        return typeof label === 'function' ? label(context) : label;
    }

    private static createDefaultField<TModel>(
        context: ReactiveRepeatableFieldContext<TModel>,
        config: ReactiveRepeatableFieldFactoryConfig<TModel>,
    ): FormField<TModel> {
        const width = context.column.width ?? config.width ?? DEFAULT_REPEATABLE_FIELD_WIDTH;
        const type = context.column.type ?? ReactiveInputType.TEXT;

        return ReactiveFieldFactory.create<TModel>({
            name: context.name,
            label: context.label,
            type,
            width,
            order: context.order,
            conditions: {
                ...(config.conditions ?? {}),
                ...(context.column.conditions ?? {}),
            },
            extra: {
                ...(context.column.extra ?? {}),
                field: context.name,
            },
        });
    }
}
